// Maps an Applications table column to the filter its header offers (BSCPF-6999). The header
// filters read and write the same ApplicationFilters the Filters drawer edits, so the two
// always show the same state.
import { ApplicationStatus, ScreeningStatus } from "shared/types";
import { applicationStatusTitle } from "shared/app/ApplicationStatusBadge/title";
import { NO_UNIT_TYPE } from "../constants";
import type { ApplicationFilters } from "../hooks/useApplicationQueryParams";
import type { ApplicationFilterOptions } from "../hooks/useApplicationFilterOptions";
import { getFilterDisplayName } from "./getFilterDisplayName";
import type { DateFilterKey, DateRange } from "./dateRanges";

export type ValueFilterOption = {
  value: string;
  label: string;
  count?: number;
};

type ValuesFilterConfig = {
  kind: "values";
  title: string;
  searchable: boolean;
  /**
   * Every application has one of the options (or a "none" option such as No assignee), so
   * ticking them all filters nothing out and applies as "no filter". False for Stage, Property
   * group, State and Screening status, whose lists have no blank option: ticking all of them
   * must still leave out applications without a value.
   */
  coversEveryApplication?: boolean;
  options: ValueFilterOption[];
  selected: string[];
} & (
  | {
      /** Folds the picked values into ApplicationFilters. */
      apply: (
        prev: ApplicationFilters | undefined,
        values: string[]
      ) => ApplicationFilters;
      applyDirect?: never;
    }
  | {
      /** For a filter that lives outside ApplicationFilters (the toolbar's assignee filter). */
      applyDirect: (values: string[]) => void;
      apply?: never;
    }
);

type ColumnFilterConfig =
  | ValuesFilterConfig
  | {
      kind: "date";
      title: string;
      dateKey: DateFilterKey;
      range?: DateRange;
      apply: (
        prev: ApplicationFilters | undefined,
        range?: DateRange
      ) => ApplicationFilters;
    };

export type ColumnFilterContextValue = {
  filters?: ApplicationFilters;
  filterOptions?: ApplicationFilterOptions;
  // Stage tabs already pin the stage; a stage filter would fight them.
  isStageTabSelected: boolean;
  // Status tabs (Submitted, Approved…) pin the status the same way.
  isStatusTabSelected?: boolean;
  labels: { property: string; unit: string };
  // The toolbar's assignee filter, which the Assigned to header shares.
  assignee?: {
    options: ValueFilterOption[];
    selectedIds: number[] | null;
    setSelectedIds: (ids: number[] | null) => void;
  };
};

type FilterBuilder = (
  title: string,
  context: ColumnFilterContextValue
) => ColumnFilterConfig | null;

// The order of the status tabs. Partially submitted is derived from the applicants and stored as
// `started`, so filter_options' `started` count covers both: show no count for either.
const statusFilterOptions = (
  statuses: { status: string; count: number }[]
): ValueFilterOption[] =>
  statuses.flatMap(({ status, count }) => {
    const option = {
      value: status,
      label: applicationStatusTitle({ status: status as ApplicationStatus }),
      count: status === ApplicationStatus.Started ? undefined : count
    };
    return status === ApplicationStatus.Started
      ? [
          option,
          {
            value: ApplicationStatus.PartiallySubmitted,
            label: applicationStatusTitle({
              status: ApplicationStatus.PartiallySubmitted
            })
          }
        ]
      : [option];
  });

const DATE_COLUMNS: Record<string, DateFilterKey> = {
  submitted_at: "submitted_at",
  started_at: "started_at",
  updated_at: "updated_at",
  desired_move_in_date: "desired_move_in_date"
};

const nonEmpty = <T>(values: T[]) => (values.length ? values : undefined);

const toNamedItems = (
  values: string[],
  options: ValueFilterOption[]
): { id: string; name: string }[] =>
  values.map((value) => ({
    id: value,
    name: options.find((option) => option.value === value)?.label ?? value
  }));

const dateFilter = (
  dateKey: DateFilterKey,
  title: string,
  filters?: ApplicationFilters
): ColumnFilterConfig => ({
  kind: "date",
  title,
  dateKey,
  range: filters?.dateRanges?.[dateKey],
  apply: (prev, range) => {
    const { [dateKey]: _previous, ...rest } = prev?.dateRanges ?? {};
    const next =
      range?.from || range?.to ? { ...rest, [dateKey]: range } : rest;
    return { ...prev, dateRanges: Object.keys(next).length ? next : undefined };
  }
});

const statusFilter: FilterBuilder = (
  title,
  { filters, filterOptions, isStatusTabSelected }
) => {
  if (isStatusTabSelected || !filterOptions?.statuses) return null;
  return {
    kind: "values",
    title,
    searchable: true,
    coversEveryApplication: true,
    options: statusFilterOptions(filterOptions.statuses),
    selected: filters?.status ?? [],
    apply: (prev, values) => ({
      ...prev,
      status: nonEmpty(values as ApplicationStatus[])
    })
  };
};

const stateFilter: FilterBuilder = (title, { filters, filterOptions }) => {
  if (!filterOptions?.states) return null;
  return {
    kind: "values",
    title,
    searchable: true,
    options: filterOptions.states.map(({ state, count }) => ({
      value: state,
      label: state,
      count
    })),
    selected: filters?.states ?? [],
    apply: (prev, values) => ({ ...prev, states: nonEmpty(values) })
  };
};

const assigneeFilter: FilterBuilder = (title, { assignee }) => {
  if (!assignee) return null;
  return {
    kind: "values",
    title,
    searchable: true,
    coversEveryApplication: true,
    options: assignee.options,
    selected: (assignee.selectedIds ?? []).map(String),
    applyDirect: (values) =>
      assignee.setSelectedIds(values.length ? values.map(Number) : null)
  };
};

const screeningStatusFilter: FilterBuilder = (
  title,
  { filters, filterOptions }
) => {
  if (!filterOptions?.screening_statuses) return null;
  return {
    kind: "values",
    title,
    searchable: true,
    options: filterOptions.screening_statuses.map(({ status, count }) => ({
      value: status,
      label: getFilterDisplayName(status),
      count
    })),
    selected: filters?.screeningStatuses ?? [],
    apply: (prev, values) => ({
      ...prev,
      screeningStatuses: nonEmpty(values as ScreeningStatus[])
    })
  };
};

const propertyFilter: FilterBuilder = (
  _title,
  { filters, filterOptions, labels }
) => {
  if (!filterOptions?.properties) return null;
  const options = filterOptions.properties.map(({ id, name, count }) => ({
    value: String(id),
    label: name,
    count
  }));
  return {
    kind: "values",
    title: labels.property,
    searchable: true,
    coversEveryApplication: true,
    options,
    selected: (filters?.properties ?? []).map((p) => String(p.id)),
    apply: (prev, values) => ({
      ...prev,
      properties: nonEmpty(toNamedItems(values, options))
    })
  };
};

const propertyGroupFilter: FilterBuilder = (
  title,
  { filters, filterOptions }
) => {
  if (!filterOptions?.property_groups) return null;
  const options = filterOptions.property_groups.map(({ id, name, count }) => ({
    value: String(id),
    label: name,
    count
  }));
  return {
    kind: "values",
    title,
    searchable: true,
    options,
    selected: (filters?.propertyGroups ?? []).map((g) => String(g.id)),
    apply: (prev, values) => ({
      ...prev,
      propertyGroups: nonEmpty(toNamedItems(values, options))
    })
  };
};

const unitFilter: FilterBuilder = (
  _title,
  { filters, filterOptions, labels }
) => {
  if (!filterOptions?.units) return null;
  // The partner-wide list names the property too: unit titles such as "101" repeat.
  const options = filterOptions.units.map(
    ({ id, title, property_name, count }) => ({
      value: String(id),
      label: property_name ? `${title} · ${property_name}` : title,
      count
    })
  );
  return {
    kind: "values",
    title: labels.unit,
    searchable: true,
    options,
    selected: (filters?.units ?? []).map((u) => String(u.id)),
    apply: (prev, values) => ({
      ...prev,
      units: nonEmpty(toNamedItems(values, options))
    })
  };
};

const unitTypeFilter: FilterBuilder = (title, { filters, filterOptions }) => {
  if (!filterOptions?.unit_types) return null;
  return {
    kind: "values",
    title,
    searchable: true,
    // The blank unit type has its own option ("—").
    coversEveryApplication: true,
    options: filterOptions.unit_types.map(({ unit_type, count }) => {
      const value = unit_type === "" ? NO_UNIT_TYPE : unit_type;
      return { value, label: getFilterDisplayName(value), count };
    }),
    selected: filters?.unitTypes ?? [],
    apply: (prev, values) => ({ ...prev, unitTypes: nonEmpty(values) })
  };
};

const stageFilter: FilterBuilder = (
  title,
  { filters, filterOptions, isStageTabSelected }
) => {
  if (isStageTabSelected || !filterOptions?.stages?.length) return null;
  return {
    kind: "values",
    title,
    searchable: true,
    options: filterOptions.stages.map(({ id, name, count }) => ({
      value: String(id),
      label: name,
      count
    })),
    selected: (filters?.stageIds ?? []).map(String),
    apply: (prev, values) => ({
      ...prev,
      stageIds: nonEmpty(values.map(Number))
    })
  };
};

const VALUE_FILTERS: Record<string, FilterBuilder> = {
  status: statusFilter,
  state: stateFilter,
  assigned_to: assigneeFilter,
  screening_status: screeningStatusFilter,
  property: propertyFilter,
  property_group: propertyGroupFilter,
  unit: unitFilter,
  unit_type: unitTypeFilter,
  stage: stageFilter
};

export const getColumnFilterConfig = (
  colId: string,
  title: string,
  context: ColumnFilterContextValue
): ColumnFilterConfig | null => {
  const dateKey = DATE_COLUMNS[colId];
  if (dateKey) return dateFilter(dateKey, title, context.filters);

  return VALUE_FILTERS[colId]?.(title, context) ?? null;
};

export const hasActiveFilters = (
  filters?: ApplicationFilters,
  assigneeIds?: number[] | null
) =>
  Boolean(
    filters?.status?.length ||
    filters?.states?.length ||
    filters?.properties?.length ||
    filters?.cities?.length ||
    filters?.units?.length ||
    filters?.unitTypes?.length ||
    filters?.screeningStatuses?.length ||
    filters?.unitStatus ||
    filters?.propertyGroups?.length ||
    filters?.stageIds?.length ||
    (filters?.dateRanges && Object.keys(filters.dateRanges).length) ||
    assigneeIds?.length
  );
